import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type {
  IActionMeta,
  IReduxRequestNode,
  IZeroSearchBlockRequest,
  IZeroSearchBlockResponse,
  IZeroSearchPageState,
  IZeroSearchResponse,
} from "@litres-libs/types";
import { ZeroSearchResponseItemTypesEnum } from "@litres-libs/types";
import { Request } from "../../request/Request";

import type { APIRequestParams } from "./searchSlice";
import { getGlobalHeaders } from "../browser/browserSlice";
import type { RootState } from "../../store";
import { addHydrateReducer } from "../../utils/hydrateReducer";

const initialState: IZeroSearchPageState = {
  recommendationBlocks: [],
  loading: false,
  error: {},
  isListInitiated: false,
  isInfoInitiated: false,
  data: {
    [ZeroSearchResponseItemTypesEnum.arts]: {
      data: [],
      pagination: {
        next_page: null,
        previous_page: null,
      },
      loading: false,
    },
    [ZeroSearchResponseItemTypesEnum.persons]: {
      data: [],
      pagination: {
        next_page: null,
        previous_page: null,
      },
      loading: false,
    },
    [ZeroSearchResponseItemTypesEnum.series]: {
      data: [],
      pagination: {
        next_page: null,
        previous_page: null,
      },
      loading: false,
    },
    [ZeroSearchResponseItemTypesEnum.tags]: {
      data: [],
      pagination: {
        next_page: null,
        previous_page: null,
      },
      loading: false,
    },
  },
};

/**
 * Запрос блоков рекомендаций в нужном порядке
 **/
export const getZeroSearchRecommendationsList = createAsyncThunk(
  "global/zeroSearch/",
  async (params: any, { requestId, getState }) => {
    const url = "/api/recommendations-dashboard";

    const response = new Request.Foundation<object, IZeroSearchResponse>({
      url,
      request: {
        method: "GET",
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
      provider: {
        id: requestId,
      },
    });
    return await response.fetch();
  }
);

/**
 * Запрос содержимого блоков рекомендаций
 **/
export const getZeroSearchRecommendation = createAsyncThunk(
  "global/zeroSearch/info",
  async (
    params: APIRequestParams<{
      url: string;
      type: ZeroSearchResponseItemTypesEnum;
    }>,
    { requestId, getState }
  ) => {
    const {
      url,
      onSuccess = (): void => {},
      onError = (): void => {},
    } = params;

    const requestURL = `${url}`;

    const response = new Request.Foundation<
      IZeroSearchBlockRequest,
      IZeroSearchBlockResponse
    >({
      url: requestURL,
      request: {
        method: "GET",
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
      provider: {
        id: requestId,
      },
      onSuccess,
      onError,
    });
    return await response.fetch();
  }
);

export const zeroSearchPageSlice = createSlice({
  name: "global/zeroSearchPage",
  initialState,
  reducers: {},
  extraReducers: builder => {
    /**
     * Кейсы запроса блоков рекомендаций в нужном порядке
     **/
    builder.addCase(
      getZeroSearchRecommendationsList.pending,
      (state: IZeroSearchPageState) => {
        state.loading = true;
      }
    );

    builder.addCase(
      getZeroSearchRecommendationsList.fulfilled,
      (
        state: IZeroSearchPageState,
        action: PayloadAction<IReduxRequestNode<any, IZeroSearchResponse>>
      ) => {
        const payload = action.payload.response?.payload;

        if (!payload) {
          return;
        }

        state.recommendationBlocks = payload.data;
        state.isListInitiated = true;
      }
    );

    builder.addCase(
      getZeroSearchRecommendationsList.rejected,
      (state: IZeroSearchPageState) => {
        state.loading = false;
        state.isListInitiated = false;
      }
    );

    /**
     * Кейсы запроса содержимого блока рекомендаций
     **/
    builder.addCase(
      getZeroSearchRecommendation.pending,
      (
        state: IZeroSearchPageState,
        action: PayloadAction<unknown, string, IActionMeta>
      ) => {
        const dataType: ZeroSearchResponseItemTypesEnum = action.meta.arg.type;
        state.data[dataType].loading = true;
      }
    );

    builder.addCase(
      getZeroSearchRecommendation.fulfilled,
      (
        state: IZeroSearchPageState,
        action: PayloadAction<
          IReduxRequestNode<IZeroSearchBlockRequest, IZeroSearchBlockResponse>,
          string,
          IActionMeta
        >
      ) => {
        const data = action.payload.response?.payload;
        const dataType: ZeroSearchResponseItemTypesEnum = action.meta.arg.type;

        if (!data) {
          return;
        }
        state.data[dataType].pagination = data.pagination;
        state.data[dataType].data = [
          ...state.data[dataType].data,
          ...data.data,
        ];
        state.data[dataType].loading = false;
        state.isInfoInitiated = true;
      }
    );

    builder.addCase(
      getZeroSearchRecommendation.rejected,
      (
        state: IZeroSearchPageState,
        action: PayloadAction<unknown, string, IActionMeta>
      ) => {
        state.isInfoInitiated = false;
        const dataType: ZeroSearchResponseItemTypesEnum = action.meta.arg.type;
        state.data[dataType].loading = false;
      }
    );

    addHydrateReducer(builder, "global.zeroSearchPage");
  },
});

export default zeroSearchPageSlice.reducer;
